import { useEffect, useState } from "react";
import { useFormik } from "formik";
import { Checkbox, Stack, Typography } from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { object, string, array } from "yup";
import MuiTextField from "../../components/common/text-field/MuiTextField";
import { useProtectedAxiosInstance } from "../../hooks/axiosInstance";
import errorResolver from "../../utils/error-resolver";
import { useNotificationContext } from "../../context/notificationContext";
import { ISectionSchema, RoleSchema } from "../../services/Schemas/Role";

import MuiSelect from "../../components/common/select/MuiSelect";
import { SectionListRequest } from "../../services/Endpoints/sections/SectionsListEndpoint";
import { AccessEnums } from "../../constants/accessEnum";
import { useCanAccess } from "../../hooks/canAccess";

interface ICreateRoleFrom {
  roleInfo?: ISectionSchema;
  onSuccess: () => void;
}

export default function CreateAccessForm({
  roleInfo,
  onSuccess,
}: ICreateRoleFrom) {
  const [waiting, setWaiting] = useState(false);

  const protectedInstance = useProtectedAxiosInstance();
  const { setNotification } = useNotificationContext();

  const loginSchema = object({
    caption: string().min(4, "نام ضروری است").required("نام ضروری است"),
    group: string().required("گروه ضروری است"),
    section_key: string().min(4, "بخش ضروری است").required(),
    description: string().optional(),
  });

  const { can } = useCanAccess();

  const formik = useFormik({
    initialValues: {
      caption: roleInfo?.caption ?? "",
      description: roleInfo?.description ?? "",
      group: "admin",
      section_key: roleInfo?.section_key ?? "",
    },
    validationSchema: loginSchema,
    onSubmit: async (values) => {
      setWaiting(true);
      console.log(values);
      try {
        const { caption, description, group, section_key } = values;

        const contactUpdateRequest = new SectionListRequest(protectedInstance);

        const resp =
          roleInfo && roleInfo.id
            ? await contactUpdateRequest.update(roleInfo.id.toString(), {
                caption,
                description,
                group,
                section_key,
              })
            : await contactUpdateRequest.create({
                caption,
                description,
                group,
                section_key,
              });

        setWaiting(false);

        formik.resetForm();
        setNotification({
          type: "success",
          message: `دسترسی با موفقیت ${roleInfo ? "آپدیت" : "ایجاد"} شد.`,
        });
        onSuccess();
      } catch (error: any) {
        errorResolver({ error, setNotification });
        setWaiting(false);
      }
    },
    enableReinitialize: true,
  });

  return (
    <Stack
      maxWidth="100%"
      width="100%"
      gap="20.31px"
      mx="auto"
      component="form"
      onSubmit={formik.handleSubmit}
    >
      <MuiTextField
        width="100%"
        label={"نام"}
        placeholder={"نام دسترسی جدید را وارد کنید"}
        name={"caption"}
        type="text"
        value={formik.values.caption}
        handleChange={formik.handleChange}
        error={
          formik.touched.caption &&
          Boolean(formik.errors.caption) &&
          formik.errors.caption
        }
      />
      <MuiTextField
        width="100%"
        label={"بخش"}
        placeholder={"بخش  را وارد کنید"}
        name={"section_key"}
        type="text"
        value={formik.values.section_key}
        handleChange={formik.handleChange}
        error={
          formik.touched.section_key &&
          Boolean(formik.errors.section_key) &&
          formik.errors.section_key
        }
      />
      <MuiSelect
        label={"گروه نقش  را انتخاب کنید"}
        name={"group"}
        placeholder={"گروه"}
        value={formik.values.group}
        handleChange={formik.handleChange}
        options={[
          {
            value: "admin",
            label: "ادمین",
          },
          {
            value: "user",
            label: "یوزر",
          },
        ]}
      />
      <MuiTextField
        multiline={true}
        width="100%"
        label={"توضیحات"}
        placeholder={"توضیحات  را وارد کنید"}
        name={"description"}
        type="text"
        value={formik.values.description}
        handleChange={formik.handleChange}
        error={
          formik.touched.description &&
          Boolean(formik.errors.description) &&
          formik.errors.description
        }
      />
      <LoadingButton
        disabled={!can(AccessEnums.AdminAclSectionsCreate)}
        variant="confirm"
        sx={{
          mt: "10px",
        }}
        type="submit"
        loading={waiting}
      >
        تایید
      </LoadingButton>
    </Stack>
  );
}
